/**
 * CMS admin API — media upload helpers.
 *
 * Backend contract: `POST /api/cms/upload` expects multipart/form-data with
 * a `file` field (and optional `alt_text`). Returns the Supabase Storage
 * public URL plus the `cms_media` record.
 *
 * This module exists specifically to fix the legacy bug where admin image
 * editors were passing blob: URLs into mutate payloads (see audit finding
 * S-H8): now the editor calls `uploadCmsImage(file)` first, awaits the real
 * URL, and only then commits the form.
 */
import { supabase } from "@/lib/supabase";
import { ApiError } from "@/lib/api-error-handler";

const API_URL = import.meta.env.VITE_API_URL ?? "";

interface CmsUploadResponse {
  readonly url: string;
  readonly media: Record<string, unknown> | null;
}

export async function uploadCmsImage(
  file: File | Blob,
  altText?: string,
): Promise<CmsUploadResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  const formData = new FormData();
  // Supabase Storage requires a filename with an extension. Blob uploads
  // coming from the in-browser editor may not have one — derive from MIME.
  const filename =
    file instanceof File && file.name
      ? file.name
      : inferFilenameFromMime(file.type);
  formData.append("file", file, filename);
  if (altText) formData.append("alt_text", altText);

  const res = await fetch(`${API_URL}/api/cms/upload`, {
    method: "POST",
    credentials: "include",
    // Do NOT set Content-Type — the browser will set multipart/form-data
    // with the proper boundary.
    headers: {
      "x-admin-platform": "djayma",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    const body: unknown = await res.json().catch(() => ({}));
    const rawError = (body as { error?: unknown }).error;
    const message =
      typeof rawError === "string" ? rawError : `HTTP ${res.status}`;
    throw new ApiError(res.status, message);
  }

  return (await res.json()) as CmsUploadResponse;
}

function inferFilenameFromMime(mime: string): string {
  const map: Record<string, string> = {
    "image/jpeg": "upload.jpg",
    "image/png": "upload.png",
    "image/webp": "upload.webp",
  };
  return map[mime] ?? "upload.bin";
}
